import { useMemo } from "react";
import { Link } from "react-router";
import { Loader2, ExternalLink } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useDashboardChart } from "@/hooks/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { MaskedText } from "@/components/masked-text";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { FiltersCard } from "./filters-card";

const DASHBOARD_ID = "feedback";

const GRID_COLOR = "#2B2B31";
const POSITIVE_COLOR = "#5FBF89";
const NEGATIVE_COLOR = "#E5645A";
const axisStyle = { fontSize: 11, fill: "rgba(244, 244, 245, 0.60)" };

const SURFACES = [
  "",
  "assessment",
  "amino_age",
  "self_reported_lab_visit",
  "plan",
] as const;
const SURFACE_LABELS: Record<string, string> = {
  "": "Alle flater",
  assessment: "Dybdeanalyse",
  amino_age: "Biologisk alder",
  self_reported_lab_visit: "Egenrapportert prøve",
  plan: "Plan",
};
const SURFACE_COLORS: Record<string, string> = {
  assessment: "#D0A84C",
  amino_age: "#6B8AFF",
  self_reported_lab_visit: "#5FBF89",
  plan: "#B073C4",
};

const PLATFORMS = ["", "ios", "android", "web"] as const;
const PLATFORM_LABELS: Record<string, string> = {
  "": "Alle plattformer",
  ios: "iOS",
  android: "Android",
  web: "Web",
};

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0]!;
}

type SummaryRow = {
  total: number | string;
  positive: number | string;
  negative: number | string;
  negative_with_text: number | string;
  prev_total: number | string;
  prev_positive: number | string;
};

type WeeklyRow = {
  week: string;
  surface: string;
  total: number | string;
  positive: number | string;
};

type PlatformRow = {
  platform: string;
  positive: number | string;
  negative: number | string;
};

type SurfaceSummaryRow = {
  surface: string;
  total: number | string;
  positive: number | string;
  negative: number | string;
  negative_with_text: number | string;
};

type NegativeFeedbackRow = {
  id: string;
  created_at: string;
  auth_user_id: string;
  given_name: string | null;
  family_name: string | null;
  email: string | null;
  surface: string;
  platform: string;
  text: string | null;
  lab_visit_id: string | null;
};

const SURFACE_SUMMARY_COLUMNS: DashboardTableColumn<SurfaceSummaryRow>[] = [
  {
    id: "surface",
    label: "Flate",
    value: (r) => SURFACE_LABELS[r.surface] ?? r.surface,
  },
  { id: "total", label: "Svar", value: (r) => r.total, numeric: true, align: "right" },
  {
    id: "positive_share",
    label: "Positiv andel",
    value: (r) => pct(Number(r.positive), Number(r.total)),
    numeric: true,
    align: "right",
  },
  { id: "negative", label: "Negative", value: (r) => r.negative, numeric: true, align: "right" },
  {
    id: "negative_with_text",
    label: "Med tekst",
    value: (r) => r.negative_with_text,
    numeric: true,
    align: "right",
  },
];

// "Flate" and "Plattform" are not column-filtered here: the page's filter
// chips above already cover both, and a second filter for the same dimension
// would just compete with them.
const NEGATIVE_FEEDBACK_COLUMNS: DashboardTableColumn<NegativeFeedbackRow>[] = [
  {
    id: "created_at",
    label: "Dato",
    value: (r) => r.created_at,
    headClassName: "w-36",
  },
  {
    id: "name",
    label: "Bruker",
    value: (r) => [r.given_name, r.family_name].filter(Boolean).join(" "),
    headClassName: "w-56",
  },
  {
    id: "surface",
    label: "Flate",
    value: (r) => SURFACE_LABELS[r.surface] ?? r.surface,
    headClassName: "w-44",
  },
  {
    id: "platform",
    label: "Plattform",
    value: (r) => PLATFORM_LABELS[r.platform] ?? r.platform,
    headClassName: "w-24",
  },
  { id: "text", label: "Tilbakemelding", value: (r) => r.text ?? "" },
];

export function FeedbackDashboard() {
  const [{ from, to, surface, platform, onlyText }, update] =
    useDashboardUrlState({
      from: isoDaysAgo(90),
      to: isoDaysAgo(0),
      surface: "",
      platform: "",
      onlyText: "",
    });

  const rangeParams = useMemo(
    () => ({
      from,
      to,
      surface: surface || undefined,
      platform: platform || undefined,
    }),
    [from, to, surface, platform],
  );
  const negativeParams = useMemo(
    () => ({ ...rangeParams, onlyWithText: onlyText === "1" || undefined }),
    [rangeParams, onlyText],
  );

  const summary = useDashboardChart<typeof rangeParams, SummaryRow>(
    DASHBOARD_ID,
    "summary",
    rangeParams,
  );
  const weekly = useDashboardChart<typeof rangeParams, WeeklyRow>(
    DASHBOARD_ID,
    "weekly",
    rangeParams,
  );
  const platformSplit = useDashboardChart<typeof rangeParams, PlatformRow>(
    DASHBOARD_ID,
    "platform-split",
    rangeParams,
  );
  const surfaceSummary = useDashboardChart<typeof rangeParams, SurfaceSummaryRow>(
    DASHBOARD_ID,
    "surface-summary",
    rangeParams,
  );
  const negatives = useDashboardChart<typeof negativeParams, NegativeFeedbackRow>(
    DASHBOARD_ID,
    "negative-feedback",
    negativeParams,
  );

  const surfaceSummaryTable = useDashboardTable(
    "surface-summary",
    SURFACE_SUMMARY_COLUMNS,
    surfaceSummary.data,
  );
  const negativesTable = useDashboardTable(
    "negatives",
    NEGATIVE_FEEDBACK_COLUMNS,
    negatives.data,
  );

  const summaryRow = summary.data?.[0];

  // Surfaces that actually have data in the window, in fixed order, so the
  // trend charts only draw series that exist.
  const activeSurfaces = useMemo(() => {
    const present = new Set(weekly.data?.map((r) => r.surface));
    return SURFACES.filter((s) => s && present.has(s));
  }, [weekly.data]);

  // Pivot week × surface rows into one object per week: counts for the
  // stacked volume chart and positive share (%) for the sentiment lines.
  const { volumeData, shareData } = useMemo(() => {
    const byWeek = new Map<string, WeeklyRow[]>();
    for (const r of weekly.data ?? []) {
      const list = byWeek.get(r.week) ?? [];
      list.push(r);
      byWeek.set(r.week, list);
    }
    const volume: Record<string, unknown>[] = [];
    const share: Record<string, unknown>[] = [];
    for (const [week, rows] of byWeek) {
      const v: Record<string, unknown> = { week };
      const s: Record<string, unknown> = { week };
      for (const r of rows) {
        const total = Number(r.total);
        v[r.surface] = total;
        if (total > 0) {
          s[r.surface] = Math.round((Number(r.positive) / total) * 100);
        }
      }
      volume.push(v);
      share.push(s);
    }
    return { volumeData: volume, shareData: share };
  }, [weekly.data]);

  const positiveShare = summaryRow
    ? pct(Number(summaryRow.positive), Number(summaryRow.total))
    : null;
  const prevShare = summaryRow
    ? pct(Number(summaryRow.prev_positive), Number(summaryRow.prev_total))
    : null;

  return (
    <div className="space-y-6">
      <FiltersCard>
        <div className="flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Fra</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => update({ from: e.target.value })}
              className="w-44"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-admin-text-subtle">Til</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => update({ to: e.target.value })}
              className="w-44"
            />
          </label>
        </div>
        <div>
          <p className="text-xs text-admin-text-subtle mb-2">Flate</p>
          <div className="flex flex-wrap gap-2">
            {SURFACES.map((s) => (
              <Button
                key={s || "all"}
                variant={surface === s ? "default" : "outline"}
                size="sm"
                onClick={() => update({ surface: s })}
              >
                {SURFACE_LABELS[s]}
              </Button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs text-admin-text-subtle mb-2">Plattform</p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <Button
                key={p || "all"}
                variant={platform === p ? "default" : "outline"}
                size="sm"
                onClick={() => update({ platform: p })}
              >
                {PLATFORM_LABELS[p]}
              </Button>
            ))}
          </div>
        </div>
      </FiltersCard>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {summary.isLoading ? (
          <div className="col-span-full">
            <ChartSkeleton />
          </div>
        ) : summary.isError ? (
          <div className="col-span-full">
            <ErrorBlock message={summary.error?.message} />
          </div>
        ) : (
          <>
            <KpiTile
              label="Tilbakemeldinger"
              value={fmt(summaryRow?.total)}
              help="Antall svar på feedback-kortene i perioden."
            />
            <KpiTile
              label="Positiv andel"
              value={positiveShare == null ? "–" : `${positiveShare}%`}
              help={
                prevShare == null
                  ? "Andel tommel opp. Ingen data i forrige periode å sammenligne med."
                  : `Andel tommel opp. Forrige periode av samme lengde: ${prevShare}%.`
              }
            />
            <KpiTile
              label="Negative"
              value={fmt(summaryRow?.negative)}
              help="Antall tommel ned i perioden."
            />
            <KpiTile
              label="Negative med tekst"
              value={fmt(summaryRow?.negative_with_text)}
              help="Tommel ned der brukeren skrev hva som kunne vært bedre."
            />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Positiv andel per uke</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Andel tommel opp per uke, én linje per flate. Et fall på én
              flate tyder på et problem med akkurat den skjermen. Se
              volumsøylene ved siden av, få svar gir store utslag.
            </p>
          </CardHeader>
          <CardContent>
            <ChartState query={weekly}>
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart
                    data={shareData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey="week" tick={axisStyle} />
                    <YAxis
                      tick={axisStyle}
                      tickFormatter={(v: number) => `${v}%`}
                      domain={[0, 100]}
                      width={40}
                    />
                    <Tooltip content={<WeeklyTooltip unit="%" />} />
                    {activeSurfaces.map((s) => (
                      <Line
                        key={s}
                        type="monotone"
                        dataKey={s}
                        name={SURFACE_LABELS[s]}
                        stroke={SURFACE_COLORS[s]}
                        strokeWidth={2}
                        dot={{ fill: SURFACE_COLORS[s], r: 3 }}
                        activeDot={{ r: 5 }}
                        connectNulls
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                <SurfaceLegend surfaces={activeSurfaces} />
              </div>
            </ChartState>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Volum per uke</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Antall tilbakemeldinger per uke, stablet per flate.
            </p>
          </CardHeader>
          <CardContent>
            <ChartState query={weekly}>
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={volumeData}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey="week" tick={axisStyle} />
                    <YAxis tick={axisStyle} allowDecimals={false} width={36} />
                    <Tooltip
                      content={<WeeklyTooltip />}
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    />
                    {activeSurfaces.map((s) => (
                      <Bar
                        key={s}
                        dataKey={s}
                        name={SURFACE_LABELS[s]}
                        stackId="volume"
                        fill={SURFACE_COLORS[s]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                <SurfaceLegend surfaces={activeSurfaces} />
              </div>
            </ChartState>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Per plattform</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Positive og negative tilbakemeldinger per plattform. Skjevhet
              på én plattform kan tyde på en plattformspesifikk feil.
            </p>
          </CardHeader>
          <CardContent>
            <ChartState query={platformSplit}>
              <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={(platformSplit.data ?? []).map((r) => ({
                      platform: PLATFORM_LABELS[r.platform] ?? r.platform,
                      Positive: Number(r.positive),
                      Negative: Number(r.negative),
                    }))}
                    margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey="platform" tick={axisStyle} />
                    <YAxis tick={axisStyle} allowDecimals={false} width={36} />
                    <Tooltip
                      content={<WeeklyTooltip />}
                      cursor={{ fill: "rgba(255,255,255,0.04)" }}
                    />
                    <Bar
                      dataKey="Positive"
                      fill={POSITIVE_COLOR}
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="Negative"
                      fill={NEGATIVE_COLOR}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartState>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Per flate</CardTitle>
            <p className="text-xs text-admin-text-subtle mt-1">
              Oppsummering per flate i valgt periode.
            </p>
          </CardHeader>
          <CardContent>
            <ChartState query={surfaceSummary}>
              <DashboardTable
                table={surfaceSummaryTable}
                columns={SURFACE_SUMMARY_COLUMNS}
                renderRow={(r) => (
                  <TableRow key={r.surface}>
                    <TableCell>
                      {SURFACE_LABELS[r.surface] ?? r.surface}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(r.total)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pct(Number(r.positive), Number(r.total))}%
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(r.negative)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmt(r.negative_with_text)}
                    </TableCell>
                  </TableRow>
                )}
              />
            </ChartState>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Negative tilbakemeldinger</CardTitle>
          <p className="text-xs text-admin-text-subtle mt-1">
            Alle tommel ned i valgt periode, nyeste først. Klikk på brukeren
            for å følge opp.
          </p>
          <div className="mt-3">
            <Button
              variant={onlyText === "1" ? "default" : "outline"}
              size="sm"
              onClick={() => update({ onlyText: onlyText === "1" ? "" : "1" })}
            >
              Kun med tekst
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ChartState query={negatives} emptyText="Ingen negative tilbakemeldinger i perioden.">
            <DashboardTable
              table={negativesTable}
              columns={NEGATIVE_FEEDBACK_COLUMNS}
              renderRow={(r) => {
                const name = [r.given_name, r.family_name]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap tabular-nums text-admin-text-secondary">
                      {r.created_at}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/anamnesis/${r.auth_user_id}`}
                        className="group inline-flex flex-col"
                      >
                        <span className="inline-flex items-center gap-1 text-admin-primary group-hover:underline">
                          {name ? <MaskedText value={name} /> : "Ukjent navn"}
                          <ExternalLink className="h-3 w-3" />
                        </span>
                        {r.email && (
                          <span className="text-xs text-admin-text-subtle">
                            <MaskedText value={r.email} type="email" />
                          </span>
                        )}
                      </Link>
                    </TableCell>
                    <TableCell className="text-admin-text-secondary">
                      {SURFACE_LABELS[r.surface] ?? r.surface}
                    </TableCell>
                    <TableCell className="text-admin-text-secondary">
                      {PLATFORM_LABELS[r.platform] ?? r.platform}
                    </TableCell>
                    <TableCell>
                      {r.text?.trim() ? (
                        <p className="whitespace-pre-wrap max-w-2xl">
                          {r.text}
                        </p>
                      ) : (
                        <span className="text-admin-text-subtle italic">
                          Ingen tekst
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              }}
            />
            {(negatives.data?.length ?? 0) >= 500 && (
              <p className="text-xs text-admin-text-subtle mt-2">
                Viser de 500 nyeste. Snevre inn datoperioden for å se eldre.
              </p>
            )}
          </ChartState>
        </CardContent>
      </Card>
    </div>
  );
}

function pct(part: number, total: number): number | null {
  if (!total) return null;
  return Math.round((part / total) * 100);
}

function fmt(value: number | string | undefined): string {
  return Number(value ?? 0).toLocaleString("no-NO");
}

function KpiTile({
  label,
  value,
  help,
}: {
  label: string;
  value: string;
  help: string;
}) {
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card p-4">
      <p className="text-xs text-admin-text-subtle">{label}</p>
      <p className="text-3xl font-semibold tabular-nums text-admin-text-primary mt-1">
        {value}
      </p>
      <p className="text-xs text-admin-text-subtle mt-2 leading-snug">{help}</p>
    </div>
  );
}

function SurfaceLegend({ surfaces }: { surfaces: string[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-4">
      {surfaces.map((s) => (
        <span
          key={s}
          className="inline-flex items-center gap-1.5 text-xs text-admin-text-secondary"
        >
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: SURFACE_COLORS[s] }}
          />
          {SURFACE_LABELS[s]}
        </span>
      ))}
    </div>
  );
}

// Injected by recharts' <Tooltip content={...}>; recharts' prop types for
// custom content don't fit this usage, so declare the shape we consume.
type WeeklyTooltipProps = {
  active?: boolean;
  payload?: {
    dataKey?: string | number;
    name?: string;
    value?: number | string;
    color?: string;
  }[];
  label?: string;
  unit?: string;
};

function WeeklyTooltip({ active, payload, label, unit }: WeeklyTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="text-xs" style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toLocaleString("no-NO")}
          {unit ?? ""}
        </p>
      ))}
    </div>
  );
}

function ChartState({
  query,
  emptyText = "Ingen data i perioden.",
  children,
}: {
  query: { isLoading: boolean; isError: boolean; error?: Error | null; data?: unknown[] };
  emptyText?: string;
  children: React.ReactNode;
}) {
  if (query.isLoading) return <ChartSkeleton />;
  if (query.isError) return <ErrorBlock message={query.error?.message} />;
  if (!query.data || query.data.length === 0)
    return <EmptyBlock>{emptyText}</EmptyBlock>;
  return <>{children}</>;
}

function ChartSkeleton() {
  return (
    <div className="flex items-center gap-2 text-admin-text-subtle text-sm py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" />
      Laster…
    </div>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm text-admin-text-subtle py-6 text-center">
      {children}
    </p>
  );
}

function ErrorBlock({ message }: { message?: string }) {
  return (
    <div className="rounded-md border border-admin-danger-500/30 bg-admin-danger-500/5 p-4 text-sm text-admin-danger-400">
      {message || "Ukjent feil"}
    </div>
  );
}
